import { useContext, useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import LanguageContext from "../components/LanguageContext";
import { useNavigate } from "react-router-dom";

function Home() {
    const { t, lang } = useContext(LanguageContext);
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef(null);
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (diff > 50) next();
        else if (diff < -50) prev();
        touchStartX.current = null;
    };

    const services = t.services || [];
    const navigate = useNavigate();

    // Slideshow
    useEffect(() => {
        setCurrentIndex(0);
    }, [lang]);

    const goTo = (index) => {
        setCurrentIndex(index)
    };

    const next = () => {
        setCurrentIndex((prev) => (prev + 1) % services.length)
    };

    const prev = () => {
        setCurrentIndex((prev) => prev === 0 ? services.length - 1 : prev - 1)
    };

    const currentService = services[currentIndex] || {};

    return (
        <motion.div className="page"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            {/* Hero Section */}
            <section className="hero">
                <motion.div className="hero-text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <h1>{t.heroTitle}</h1>
                    <p className="hero-text">{t.heroText}</p>
                </motion.div>
            </section>

            {/* Services Slideshow */}
            {
                services.length > 0 && (
                    <section className="slideshow">
                        <div className="slideshow-card-wrapper"
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            <motion.article
                                key={currentIndex + "-" + lang}
                                onClick={() => navigate('/services')}
                                style={{ cursor: 'pointer' }}
                                className="slideshow-card"
                                initial={{ opacity: 0, x: 25 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -15 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {currentService.image && (
                                    <div className="slideshow-image-wrapper">
                                        <img
                                            src={currentService.image}
                                            alt={currentService.name}
                                            className="slideshow-image"
                                        />
                                    </div>
                                )}

                                <h3>{currentService.name}</h3>
                                <p className="slideshow-desc">{currentService.desc}</p>
                                <span className="slideshow-duration">
                                    {currentService.duration}
                                </span>
                            </motion.article>
                        </div>

                        <div className="slideshow-controls">
                            <button type="button" className="slide-arrow"
                                onClick={prev}
                            >
                                ‹
                            </button>

                            <div className="slideshow-dots">
                                {services.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={"dot" + (index === currentIndex ? " active" : "")}
                                        onClick={() => goTo(index)}
                                    />
                                ))}
                            </div>

                            <button type="button" className="slide-arrow"
                                onClick={next}
                            >
                                ›
                            </button>
                        </div>
                    </section>
                )
            }

            {/* Info Cards */}
            <motion.section
                className="home-cards"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.2 } }
                }}>

                <motion.div className="home-card"
                    variants={{
                        hidden: { opacity: 0, y: 40 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
                    }}>
                    <div className="home-card-icon"><img src="/images/icon_who.png" alt="Who We Are" className="home-card-img" /></div>
                    <h3 className="home-card-title">{lang === 'zh' ? '关于我们' : 'Who We Are'}</h3>
                    <p className="home-card-text">{lang === 'zh'
                        ? '在心愈养生馆，我们专注于传统中式经络调理，为每位顾客提供专业舒适的养生体验。'
                        : 'At Xin Yu Wellness, we specialises in traditional Chinese meridian therapy for a professional and comfortable experience.'}</p>
                </motion.div>

                <motion.div className="home-card"
                    variants={{
                        hidden: { opacity: 0, y: 40 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
                    }}>
                    <div className="home-card-icon"><img src="/images/icon_where.png" alt="Where We Are" className="home-card-img" /></div>
                    <h3 className="home-card-title">{lang === 'zh' ? '我们在哪里' : 'Where We Are'}</h3>
                    <p className="home-card-text">{lang === 'zh'
                        ? '1 Park Road #03-40B\n珍珠坊\n新加坡 059108\n每日营业 9:30AM – 9:30PM'
                        : '1 Park Road #03-40B\nPeople\'s Park Complex\nSingapore 059108\nOpen Daily 9:30AM – 9:30PM'}</p>
                    <a href="https://maps.app.goo.gl/Y15jK8YjyrtvGYLy9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-card-btn"
                    >
                        {lang === 'zh' ? ' 获取路线' : ' Get Directions'}
                    </a>
                </motion.div>

                <motion.div className="home-card"
                    variants={{
                        hidden: { opacity: 0, y: 40 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
                    }}>
                    <div className="home-card-icon"><img src="/images/icon_why.png" alt="Why Choose Us" className="home-card-img" /></div>
                    <h3 className="home-card-title">{lang === 'zh' ? '为什么选择我们' : 'Why Choose Us'}</h3>
                    <ul className="home-card-list">
                        {(lang === 'zh' ? [
                            '经验丰富的专业理疗师',
                            '传统中式经络手法',
                            '价格实惠，物超所值',
                            '一对一个性化调理',
                            '无需特定预约，随时欢迎'
                        ] : [
                            'Experienced professional therapists',
                            'Traditional Chinese meridian techniques',
                            'Affordable and great value',
                            'Personalised one-on-one care',
                            'No appointment required, walk-ins welcome'
                        ]).map((item, i) => (
                            <li key={i} className="home-card-list-item">
                                <span className="home-card-tick">✓</span> {item}
                            </li>
                        ))}
                    </ul>
                </motion.div>
            </motion.section>
        </motion.div >
    );
}

export default Home;